import React, { useEffect, useState } from 'react';
import HeaderLogged from './components/header_logged';
import api from './api/client';
import AdminTabs from './components/AdminTabs';
import PersonalAccessEditor from './components/PersonalAccessEditor';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus, faKey, faUserSlash, faUserCheck, faCircleExclamation, faUserShield, faLaptop, faShieldHalved,
    faRobot, faPaperPlane, faEye, faEyeSlash, faCopy, faTrash, faBan, faCircleCheck, faFileLines, faRotateRight,
    faHourglassHalf,
} from '@fortawesome/free-solid-svg-icons';
import './admin-panel.css';

const emptyCreateForm = { employeeName: '', employeeSurname: '', email: '', login: '', password: '', role_id: '' };
const emptyGrantForm = { login: '', password: '', role_id: '' };
const emptyBotForm = { name: '', type: 'agent', token: '', comment: '' };

// Реальные области агентского API — значения ROUTE_SCOPES в server/routes/agent-api.js.
// Прочие разделы боту ничего не открывают, поэтому в редакторе их не показываем.
const AGENT_SCOPE_KEYS = ['product-cogs', 'general', 'material-orders', 'inventory-fba', 'shipments', 'yoy-forecast', 'production-plan'];

// Страница /admin/users (раздел admin-users) — бывшая модалка Manage Users.
function AdminUsersPage() {
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [pages, setPages] = useState([]);
    const [accessFor, setAccessFor] = useState(null);
    const [sessionsFor, setSessionsFor] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [error, setError] = useState(null);

    const [showCreateForm, setShowCreateForm] = useState(false);
    const [createForm, setCreateForm] = useState(emptyCreateForm);

    const [grantingFor, setGrantingFor] = useState(null);
    const [grantForm, setGrantForm] = useState(emptyGrantForm);

    const [resettingFor, setResettingFor] = useState(null);
    const [resetPassword, setResetPassword] = useState('');

    // Доступ до даты (Epic 8 фаза 4, сезонные сотрудники)
    const [expiryFor, setExpiryFor] = useState(null);
    const [expiryDate, setExpiryDate] = useState('');

    // Области доступа бота к агентскому API (Epic 9): список page_key
    const [scopesFor, setScopesFor] = useState(null);
    const [scopesDraft, setScopesDraft] = useState([]);
    const [scopesMsg, setScopesMsg] = useState('');

    // Боты и API-ключи (таблица bots)
    const [bots, setBots] = useState([]);
    const [showBotForm, setShowBotForm] = useState(false);
    const [botForm, setBotForm] = useState(emptyBotForm);
    const [newBotKey, setNewBotKey] = useState(null);      // { name, key } — показывается один раз
    const [revealedKeys, setRevealedKeys] = useState({});  // { [id]: полный ключ }
    const [modelDrafts, setModelDrafts] = useState({});    // { [id]: значение input до blur }
    const [instructionsFor, setInstructionsFor] = useState(null); // id бота с открытым редактором
    const [instructionsDraft, setInstructionsDraft] = useState('');
    const [allowedDraft, setAllowedDraft] = useState('');         // TG user IDs через запятую
    const [instructionsMsg, setInstructionsMsg] = useState('');
    const [botNotice, setBotNotice] = useState('');           // «proof-bot restarted» у заголовка секции
    const [restartingBot, setRestartingBot] = useState(null); // id бота, чей рестарт в полёте

    const fetchUsers = () => {
        api.get('/api/admin/users')
            .then(r => setUsers(r.data))
            .catch(err => setError(err.response?.data?.error || 'Failed to load users'));
    };

    const fetchBots = () => {
        api.get('/api/admin/bots')
            .then(r => setBots(r.data))
            .catch(err => setError(err.response?.data?.error || 'Failed to load bots'));
    };

    useEffect(() => {
        fetchUsers();
        fetchBots();
        api.get('/api/admin/roles')
            .then(r => setRoles(r.data))
            .catch(err => setError(err.response?.data?.error || 'Failed to load roles'));
        api.get('/api/admin/pages')
            .then(r => setPages(r.data))
            .catch(err => setError(err.response?.data?.error || 'Failed to load pages'));
    }, []);

    // ── Боты ─────────────────────────────────────────────────────────────────
    const handleCreateBot = (e) => {
        e.preventDefault();
        setError(null);
        api.post('/api/admin/bots', botForm)
            .then(r => {
                setShowBotForm(false);
                setBotForm(emptyBotForm);
                if (r.data.key) setNewBotKey({ name: r.data.name, key: r.data.key });
                fetchBots();
            })
            .catch(err => setError(err.response?.data?.error || 'Failed to create bot'));
    };

    const handleToggleBot = (bot) => {
        setError(null);
        api.put(`/api/admin/bots/${bot.id}`, { enabled: !bot.enabled })
            .then(fetchBots)
            .catch(err => setError(err.response?.data?.error || 'Failed to update bot'));
    };

    const handleDeleteBot = (bot) => {
        if (!window.confirm(`Delete bot "${bot.name}"?\nIts key stops working immediately.`)) return;
        setError(null);
        api.delete(`/api/admin/bots/${bot.id}`)
            .then(fetchBots)
            .catch(err => setError(err.response?.data?.error || 'Failed to delete bot'));
    };

    const toggleRevealKey = (id) => {
        if (revealedKeys[id]) {
            setRevealedKeys(prev => { const next = { ...prev }; delete next[id]; return next; });
            return;
        }
        api.post(`/api/admin/bots/${id}/reveal`)
            .then(r => setRevealedKeys(prev => ({ ...prev, [id]: r.data.key })))
            .catch(err => setError(err.response?.data?.error || 'Failed to reveal key'));
    };

    const saveBotModel = (bot) => {
        const draft = modelDrafts[bot.id];
        if (draft === undefined) return;
        if (draft === (bot.model || '')) {
            setModelDrafts(prev => { const next = { ...prev }; delete next[bot.id]; return next; });
            return;
        }
        setError(null);
        api.put(`/api/admin/bots/${bot.id}`, { model: draft })
            .then(() => api.get('/api/admin/bots'))
            .then(r => {
                setBots(r.data);
                // Черновик снимаем после обновления списка, и только если юзер
                // не начал печатать снова — иначе мигает старое значение
                setModelDrafts(prev => {
                    if (prev[bot.id] !== draft) return prev;
                    const next = { ...prev }; delete next[bot.id]; return next;
                });
            })
            .catch(err => setError(err.response?.data?.error || 'Failed to update model'));
    };

    const openInstructions = (bot) => {
        if (instructionsFor === bot.id) { setInstructionsFor(null); return; }
        setInstructionsFor(bot.id);
        setInstructionsDraft(bot.instructions || '');
        setAllowedDraft(bot.allowed_users || '');
        setInstructionsMsg('');
    };

    const saveInstructions = (bot) => {
        setError(null);
        // Шлём только изменённые поля: PUT частичный, а allowed_users параллельно
        // правит /adduser в Telegram — безусловная отправка затирала бы его записи
        const body = {};
        if (instructionsDraft !== (bot.instructions || '')) body.instructions = instructionsDraft;
        if (allowedDraft !== (bot.allowed_users || '')) body.allowed_users = allowedDraft;
        if (!Object.keys(body).length) { setInstructionsMsg('No changes'); return; }
        api.put(`/api/admin/bots/${bot.id}`, body)
            .then(() => {
                setInstructionsMsg('Saved — press ⟳ in Actions to restart the bot and apply');
                fetchBots();
            })
            .catch(err => setInstructionsMsg('Error: ' + (err.response?.data?.error || 'Failed to save')));
    };

    const openScopes = (bot) => {
        if (scopesFor === bot.id) { setScopesFor(null); return; }
        setScopesFor(bot.id);
        setScopesDraft(bot.scopes ? bot.scopes.split(',').map(s => s.trim()).filter(Boolean) : []);
        setScopesMsg('');
    };

    const saveScopes = (bot) => {
        api.put(`/api/admin/bots/${bot.id}`, { scopes: scopesDraft })
            .then(() => { setScopesMsg('Saved — applies to the next API call'); fetchBots(); })
            .catch(err => setScopesMsg('Error: ' + (err.response?.data?.error || 'Failed to save')));
    };

    const restartBot = (bot) => {
        setError(null);
        setBotNotice('');
        setRestartingBot(bot.id);
        api.post(`/api/admin/bots/${bot.id}/restart`)
            .then(() => {
                setBotNotice(`${bot.name} restarted — changes applied`);
                setInstructionsMsg('');
                setTimeout(() => setBotNotice(''), 5000);
            })
            .catch(err => setError(err.response?.data?.error || 'Failed to restart bot'))
            .finally(() => setRestartingBot(null));
    };

    const copyBotKey = async (id) => {
        setError(null);
        try {
            if (revealedKeys[id]) {
                await navigator.clipboard.writeText(revealedKeys[id]);
                return;
            }
            const keyPromise = api.post(`/api/admin/bots/${id}/reveal`).then(r => r.data.key);
            if (typeof ClipboardItem !== 'undefined') {
                // Safari теряет клик-жест после await — clipboard.write зовём синхронно,
                // а данные подъезжают промисом (санкционированный WebKit-паттерн)
                await navigator.clipboard.write([new ClipboardItem({
                    'text/plain': keyPromise.then(k => new Blob([k], { type: 'text/plain' })),
                })]);
            } else {
                await navigator.clipboard.writeText(await keyPromise);
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to copy key');
        }
    };

    const handleCreateUser = (e) => {
        e.preventDefault();
        setError(null);
        api.post('/api/admin/users', createForm)
            .then(() => {
                setShowCreateForm(false);
                setCreateForm(emptyCreateForm);
                fetchUsers();
            })
            .catch(err => setError(err.response?.data?.error || 'Failed to create user'));
    };

    const handleGrantAccess = (employeeID, e) => {
        e.preventDefault();
        setError(null);
        api.post(`/api/admin/users/${employeeID}/grant-account`, grantForm)
            .then(() => {
                setGrantingFor(null);
                setGrantForm(emptyGrantForm);
                fetchUsers();
            })
            .catch(err => setError(err.response?.data?.error || 'Failed to grant access'));
    };

    const handleRoleChange = (employeeID, role_id) => {
        setError(null);
        api.put(`/api/admin/users/${employeeID}/role`, { role_id: Number(role_id) })
            .then(fetchUsers)
            .catch(err => setError(err.response?.data?.error || 'Failed to update role'));
    };

    const handleToggleActive = (employeeID, currentIsActive) => {
        setError(null);
        api.put(`/api/admin/users/${employeeID}/active`, { isActive: currentIsActive ? 0 : 1 })
            .then(fetchUsers)
            .catch(err => setError(err.response?.data?.error || 'Failed to update status'));
    };

    // Дата включительна: в этот день человек ещё работает, со следующего — отказ.
    const handleSetExpiry = (employeeID, value) => {
        setError(null);
        api.put(`/api/admin/users/${employeeID}/expiry`, { access_expires_at: value || null })
            .then(() => { setExpiryFor(null); setExpiryDate(''); fetchUsers(); })
            .catch(err => setError(err.response?.data?.error || 'Failed to update access expiry'));
    };

    const sessionsForRef = React.useRef(null);
    const openSessions = (employeeID) => {
        if (sessionsFor === employeeID) { sessionsForRef.current = null; setSessionsFor(null); return; }
        sessionsForRef.current = employeeID;
        setSessionsFor(employeeID);
        setSessions([]);
        api.get(`/api/admin/users/${employeeID}/sessions`)
            .then(r => {
                // Отстаём от быстрых кликов: показываем только актуального сотрудника.
                if (sessionsForRef.current === employeeID) setSessions(r.data);
            })
            .catch(err => {
                if (sessionsForRef.current === employeeID) setError(err.response?.data?.error || 'Failed to load sessions');
            });
    };

    const reset2fa = (employeeID) => {
        if (!window.confirm('Reset two-factor authentication for this employee?\nThey will be asked to set it up again.')) return;
        setError(null);
        api.post(`/api/admin/users/${employeeID}/2fa/reset`)
            .then(fetchUsers)
            .catch(err => setError(err.response?.data?.error || 'Failed to reset 2FA'));
    };

    const revokeAllSessions = (employeeID) => {
        if (!window.confirm('Sign this employee out on all devices?')) return;
        api.post(`/api/admin/users/${employeeID}/sessions/revoke-all`)
            .then(() => setSessions([]))
            .catch(err => setError(err.response?.data?.error || 'Failed to revoke sessions'));
    };

    const handleResetPassword = (employeeID, e) => {
        e.preventDefault();
        setError(null);
        api.put(`/api/admin/users/${employeeID}/password`, { newPassword: resetPassword })
            .then(() => {
                setResettingFor(null);
                setResetPassword('');
            })
            .catch(err => setError(err.response?.data?.error || 'Failed to reset password'));
    };

    return (
        <>
            <HeaderLogged />
            <div className="roles-page">
                <div className="roles-page__head">
                    <h2>Users</h2>
                    <button
                        type="button"
                        className="admin-panel__new-btn"
                        onClick={() => setShowCreateForm(s => !s)}
                    >
                        <FontAwesomeIcon icon={faPlus} />
                        New Employee + Account
                    </button>
                </div>

                <AdminTabs />

                {error && (
                    <div className="admin-panel__error">
                        <FontAwesomeIcon icon={faCircleExclamation} />
                        {error}
                    </div>
                )}

                {showCreateForm && (
                    <form className="admin-panel__inline-form" onSubmit={handleCreateUser}>
                        <input placeholder="First Name" required
                            value={createForm.employeeName}
                            onChange={e => setCreateForm({ ...createForm, employeeName: e.target.value })} />
                        <input placeholder="Last Name"
                            value={createForm.employeeSurname}
                            onChange={e => setCreateForm({ ...createForm, employeeSurname: e.target.value })} />
                        <input placeholder="Email" type="email"
                            value={createForm.email}
                            onChange={e => setCreateForm({ ...createForm, email: e.target.value })} />
                        <input placeholder="Login" required
                            value={createForm.login}
                            onChange={e => setCreateForm({ ...createForm, login: e.target.value })} />
                        <input placeholder="Password" type="password" required minLength={6}
                            value={createForm.password}
                            onChange={e => setCreateForm({ ...createForm, password: e.target.value })} />
                        <select required
                            value={createForm.role_id}
                            onChange={e => setCreateForm({ ...createForm, role_id: e.target.value })}
                        >
                            <option value="" disabled>Role…</option>
                            {roles.map(r => <option key={r.id} value={r.id}>{r.title}</option>)}
                        </select>
                        <button type="submit" className="admin-panel__save-btn">Create</button>
                    </form>
                )}

                <div className="roles-page__grid-wrap">
                    <table className="admin-panel__table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Login</th>
                                <th>Role</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map(u => (
                                <React.Fragment key={u.employeeID}>
                                    <tr>
                                        <td>{u.employeeName} {u.employeeSurname || ''}</td>
                                        <td>{u.hasAccount ? u.login : <span className="admin-panel__muted">No account</span>}</td>
                                        <td>
                                            {u.hasAccount ? (
                                                <>
                                                    <select
                                                        value={u.role_id || ''}
                                                        onChange={e => handleRoleChange(u.employeeID, e.target.value)}
                                                    >
                                                        {!u.role_id && <option value="" disabled>no role</option>}
                                                        {roles.map(r => <option key={r.id} value={r.id}>{r.title}</option>)}
                                                    </select>
                                                    {u.overrides > 0 && (
                                                        <span className="admin-panel__muted" title="Personal exceptions"> +{u.overrides}</span>
                                                    )}
                                                </>
                                            ) : '—'}
                                        </td>
                                        <td>
                                            {u.hasAccount ? (
                                                <>
                                                    <span className={`admin-panel__status-badge ${u.isActive ? 'is-active' : 'is-disabled'}`}>
                                                        {u.isActive ? 'Active' : 'Disabled'}
                                                    </span>
                                                    {!!u.totp_enabled && (
                                                        <span className="admin-panel__status-badge is-active" title="Two-factor authentication is on"> 2FA</span>
                                                    )}
                                                    {u.access_expires_at && (
                                                        <span
                                                            className={`admin-panel__status-badge ${Number(u.accessExpired) ? 'is-disabled' : 'is-neutral'}`}
                                                            title="Access expiry date (inclusive)"
                                                        >
                                                            {Number(u.accessExpired) ? ' expired ' : ' until '}
                                                            {u.access_expires_at.split('-').reverse().join('.')}
                                                        </span>
                                                    )}
                                                </>
                                            ) : '—'}
                                        </td>
                                        <td className="admin-panel__actions">
                                            {u.hasAccount ? (
                                                <>
                                                    <button type="button" title="Reset password"
                                                        onClick={() => setResettingFor(resettingFor === u.employeeID ? null : u.employeeID)}>
                                                        <FontAwesomeIcon icon={faKey} />
                                                    </button>
                                                    <button type="button" title={u.isActive ? 'Revoke access' : 'Restore access'}
                                                        onClick={() => handleToggleActive(u.employeeID, u.isActive)}>
                                                        <FontAwesomeIcon icon={u.isActive ? faUserSlash : faUserCheck} />
                                                    </button>
                                                    <button type="button" title="Personal access"
                                                        onClick={() => setAccessFor(accessFor === u.employeeID ? null : u.employeeID)}>
                                                        <FontAwesomeIcon icon={faUserShield} />
                                                    </button>
                                                    <button type="button" title="Access expiry date (seasonal workers)"
                                                        onClick={() => {
                                                            if (expiryFor === u.employeeID) { setExpiryFor(null); return; }
                                                            setExpiryFor(u.employeeID);
                                                            setExpiryDate(u.access_expires_at || '');
                                                        }}>
                                                        <FontAwesomeIcon icon={faHourglassHalf} />
                                                    </button>
                                                    <button type="button" title="Active sessions"
                                                        onClick={() => openSessions(u.employeeID)}>
                                                        <FontAwesomeIcon icon={faLaptop} />
                                                    </button>
                                                    {!!u.totp_enabled && (
                                                        <button type="button" title="Reset two-factor authentication"
                                                            onClick={() => reset2fa(u.employeeID)}>
                                                            <FontAwesomeIcon icon={faShieldHalved} />
                                                        </button>
                                                    )}
                                                </>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="admin-panel__grant-btn"
                                                    onClick={() => setGrantingFor(grantingFor === u.employeeID ? null : u.employeeID)}
                                                >
                                                    Grant Access
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                    {expiryFor === u.employeeID && (
                                        <tr className="admin-panel__sub-row">
                                            <td colSpan={5}>
                                                <form className="admin-panel__inline-form"
                                                    onSubmit={(e) => { e.preventDefault(); handleSetExpiry(u.employeeID, expiryDate); }}>
                                                    <span className="admin-panel__muted">Access until (inclusive):</span>
                                                    <input type="date" value={expiryDate}
                                                        onChange={e => setExpiryDate(e.target.value)} />
                                                    <button type="submit" className="admin-panel__save-btn" disabled={!expiryDate}>Save</button>
                                                    {u.access_expires_at && (
                                                        <button type="button" className="admin-panel__grant-btn"
                                                            onClick={() => handleSetExpiry(u.employeeID, null)}>
                                                            Remove limit
                                                        </button>
                                                    )}
                                                </form>
                                            </td>
                                        </tr>
                                    )}
                                    {resettingFor === u.employeeID && (
                                        <tr className="admin-panel__sub-row">
                                            <td colSpan={5}>
                                                <form className="admin-panel__inline-form" onSubmit={(e) => handleResetPassword(u.employeeID, e)}>
                                                    <input placeholder="New password" type="password" required minLength={6}
                                                        value={resetPassword}
                                                        onChange={e => setResetPassword(e.target.value)} />
                                                    <button type="submit" className="admin-panel__save-btn">Set Password</button>
                                                </form>
                                            </td>
                                        </tr>
                                    )}
                                    {grantingFor === u.employeeID && (
                                        <tr className="admin-panel__sub-row">
                                            <td colSpan={5}>
                                                <form className="admin-panel__inline-form" onSubmit={(e) => handleGrantAccess(u.employeeID, e)}>
                                                    <input placeholder="Login" required
                                                        value={grantForm.login}
                                                        onChange={e => setGrantForm({ ...grantForm, login: e.target.value })} />
                                                    <input placeholder="Password" type="password" required minLength={6}
                                                        value={grantForm.password}
                                                        onChange={e => setGrantForm({ ...grantForm, password: e.target.value })} />
                                                    <select required
                                                        value={grantForm.role_id}
                                                        onChange={e => setGrantForm({ ...grantForm, role_id: e.target.value })}
                                                    >
                                                        <option value="" disabled>Role…</option>
                                                        {roles.map(r => <option key={r.id} value={r.id}>{r.title}</option>)}
                                                    </select>
                                                    <button type="submit" className="admin-panel__save-btn">Grant</button>
                                                </form>
                                            </td>
                                        </tr>
                                    )}
                                    {sessionsFor === u.employeeID && (
                                        <tr className="admin-panel__sub-row">
                                            <td colSpan={5}>
                                                <div className="personal-access__bar">
                                                    <span>
                                                        Active sessions: <strong>{sessions.length}</strong>
                                                    </span>
                                                    <button type="button" className="admin-panel__grant-btn"
                                                        disabled={sessions.length === 0}
                                                        onClick={() => revokeAllSessions(u.employeeID)}>
                                                        Sign out everywhere
                                                    </button>
                                                </div>
                                                {sessions.length > 0 && (
                                                    <table className="admin-panel__table">
                                                        <thead>
                                                            <tr><th>Started</th><th>Last used</th><th>Expires</th><th>IP</th><th>Device</th></tr>
                                                        </thead>
                                                        <tbody>
                                                            {sessions.map(s => (
                                                                <tr key={s.id}>
                                                                    <td className="audit-table__when">{new Date(s.created_at).toLocaleString()}</td>
                                                                    <td className="audit-table__when">{new Date(s.last_used_at).toLocaleString()}</td>
                                                                    <td className="audit-table__when">{new Date(s.expires_at).toLocaleDateString()}</td>
                                                                    <td>{s.ip || '—'}</td>
                                                                    <td className="admin-panel__muted">{(s.user_agent || '—').slice(0, 60)}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                    {accessFor === u.employeeID && (
                                        <tr className="admin-panel__sub-row">
                                            <td colSpan={5}>
                                                <PersonalAccessEditor
                                                    employeeID={u.employeeID}
                                                    pages={pages}
                                                    onSaved={fetchUsers}
                                                />
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* ── Bots & API keys ─────────────────────────────────────── */}
                <div className="roles-page__head" style={{ marginTop: 32 }}>
                    <h2>Bots &amp; API keys</h2>
                    {botNotice && <span style={{ color: '#166534', fontSize: 13 }}>{botNotice}</span>}
                    <button
                        type="button"
                        className="admin-panel__new-btn"
                        onClick={() => setShowBotForm(s => !s)}
                    >
                        <FontAwesomeIcon icon={faPlus} />
                        New Bot
                    </button>
                </div>

                {newBotKey && (
                    <div className="admin-panel__inline-form" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                        <span>API key for <strong>{newBotKey.name}</strong> — copy it now:</span>
                        <code style={{ userSelect: 'all', wordBreak: 'break-all' }}>{newBotKey.key}</code>
                        <button type="button" className="admin-panel__save-btn"
                            onClick={async () => {
                                setError(null);
                                try { await navigator.clipboard.writeText(newBotKey.key); }
                                catch { setError('Failed to copy key — select it and copy manually'); }
                            }}>
                            <FontAwesomeIcon icon={faCopy} /> Copy
                        </button>
                        <button type="button" className="admin-panel__grant-btn" onClick={() => setNewBotKey(null)}>
                            Done
                        </button>
                    </div>
                )}

                {showBotForm && (
                    <form className="admin-panel__inline-form" onSubmit={handleCreateBot}>
                        <input placeholder="Name (e.g. invoice-bot)" required
                            value={botForm.name}
                            onChange={e => setBotForm({ ...botForm, name: e.target.value })} />
                        <select
                            value={botForm.type}
                            onChange={e => setBotForm({ ...botForm, type: e.target.value })}
                        >
                            <option value="agent">AI agent — generate API key</option>
                            <option value="telegram">Telegram bot — paste token</option>
                        </select>
                        {botForm.type === 'telegram' && (
                            <>
                                <input placeholder="Telegram bot token (from BotFather)" required
                                    value={botForm.token}
                                    onChange={e => setBotForm({ ...botForm, token: e.target.value })} />
                                <input placeholder="Model (e.g. gpt-5, optional)"
                                    value={botForm.model || ''}
                                    onChange={e => setBotForm({ ...botForm, model: e.target.value })} />
                            </>
                        )}
                        <input placeholder="Comment (optional)"
                            value={botForm.comment}
                            onChange={e => setBotForm({ ...botForm, comment: e.target.value })} />
                        <button type="submit" className="admin-panel__save-btn">
                            {botForm.type === 'agent' ? 'Generate key' : 'Add bot'}
                        </button>
                    </form>
                )}

                <div className="roles-page__grid-wrap">
                    <table className="admin-panel__table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Type</th>
                                <th>Model</th>
                                <th>Key / Token</th>
                                <th>Status</th>
                                <th>Last used</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {bots.length === 0 && (
                                <tr><td colSpan={7} className="admin-panel__muted">
                                    No bots yet — add an AI agent key or a Telegram bot token
                                </td></tr>
                            )}
                            {bots.map(b => (
                                <React.Fragment key={b.id}>
                                    <tr>
                                        <td title={b.comment || undefined}>
                                            {/* nowrap: значок комментария не должен отрываться от имени */}
                                            <span style={{ whiteSpace: 'nowrap' }}>
                                                <FontAwesomeIcon
                                                    icon={b.type === 'telegram' ? faPaperPlane : faRobot}
                                                    style={{ marginRight: 6, opacity: 0.55 }}
                                                />
                                                {b.name}
                                                {b.comment && (
                                                    <FontAwesomeIcon
                                                        icon={faCircleExclamation}
                                                        style={{ marginLeft: 6, opacity: 0.35, fontSize: 11 }}
                                                    />
                                                )}
                                            </span>
                                        </td>
                                        <td>{b.type === 'telegram' ? 'Telegram' : 'AI agent'}</td>
                                        <td>
                                            {b.type === 'telegram' ? (
                                                <input
                                                    style={{
                                                        width: 110, padding: '4px 6px', fontSize: 12,
                                                        border: '1px solid #d1d5db', borderRadius: 4,
                                                    }}
                                                    placeholder="model…"
                                                    value={modelDrafts[b.id] ?? (b.model || '')}
                                                    onChange={e => setModelDrafts(prev => ({ ...prev, [b.id]: e.target.value }))}
                                                    onBlur={() => saveBotModel(b)}
                                                />
                                            ) : (
                                                <span className="admin-panel__muted">—</span>
                                            )}
                                        </td>
                                        <td>
                                            {/* Маска не переносится; длинный раскрытый ключ — переносится */}
                                            <code style={revealedKeys[b.id]
                                                ? { wordBreak: 'break-all' }
                                                : { whiteSpace: 'nowrap' }}>
                                                {revealedKeys[b.id] || b.key_masked}
                                            </code>
                                        </td>
                                        <td>
                                            <span className={`admin-panel__status-badge ${b.enabled ? 'is-active' : 'is-disabled'}`}>
                                                {b.enabled ? 'Active' : 'Disabled'}
                                            </span>
                                        </td>
                                        <td className="admin-panel__muted" style={{ whiteSpace: 'nowrap' }}>
                                            {b.last_used_at ? (
                                                <span title={new Date(b.last_used_at).toLocaleString()}>
                                                    {new Date(b.last_used_at).toLocaleDateString()}
                                                </span>
                                            ) : 'never'}
                                        </td>
                                        <td className="admin-panel__actions" style={{ whiteSpace: 'nowrap' }}>
                                            <button type="button" title={revealedKeys[b.id] ? 'Hide key' : 'Show key'}
                                                onClick={() => toggleRevealKey(b.id)}>
                                                <FontAwesomeIcon icon={revealedKeys[b.id] ? faEyeSlash : faEye} />
                                            </button>
                                            <button type="button" title="Copy key"
                                                onClick={() => copyBotKey(b.id)}>
                                                <FontAwesomeIcon icon={faCopy} />
                                            </button>
                                            {b.type === 'telegram' && (
                                                <button type="button" title="Bot instructions (system prompt)"
                                                    onClick={() => openInstructions(b)}>
                                                    <FontAwesomeIcon icon={faFileLines} />
                                                </button>
                                            )}
                                            {b.type === 'telegram' && (
                                                <button type="button" title="Restart the bot (applies saved changes)"
                                                    disabled={restartingBot === b.id}
                                                    onClick={() => restartBot(b)}>
                                                    <FontAwesomeIcon icon={faRotateRight} spin={restartingBot === b.id} />
                                                </button>
                                            )}
                                            {(b.type === 'agent' || b.name === 'invoice-bot') && (
                                                <button type="button"
                                                    title={`API access scopes (${b.scopes ? b.scopes.split(',').filter(k => k !== 'read-only').length : 0} sections${b.scopes?.split(',').includes('read-only') ? ', read-only' : ''})`}
                                                    onClick={() => openScopes(b)}>
                                                    <FontAwesomeIcon icon={faUserShield} />
                                                </button>
                                            )}
                                            <button type="button" title={b.enabled ? 'Disable' : 'Enable'}
                                                onClick={() => handleToggleBot(b)}>
                                                <FontAwesomeIcon icon={b.enabled ? faBan : faCircleCheck} />
                                            </button>
                                            <button type="button" title="Delete bot"
                                                onClick={() => handleDeleteBot(b)}>
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                        </td>
                                    </tr>
                                    {scopesFor === b.id && (
                                        <tr className="admin-panel__sub-row">
                                            <td colSpan={7}>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                    <span className="admin-panel__muted">
                                                        Which sections {b.name} may reach through the agent API.
                                                        No sections = ping only. Takes effect on the next API call.
                                                    </span>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px' }}>
                                                        {pages.filter(p => AGENT_SCOPE_KEYS.includes(p.page_key)).map(p => (
                                                            <label key={p.page_key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                                                                <input type="checkbox"
                                                                    checked={scopesDraft.includes(p.page_key)}
                                                                    onChange={() => setScopesDraft(prev =>
                                                                        prev.includes(p.page_key)
                                                                            ? prev.filter(k => k !== p.page_key)
                                                                            : [...prev, p.page_key])} />
                                                                {p.title}
                                                            </label>
                                                        ))}
                                                    </div>
                                                    {/* не раздел, а флаг: сервер пускает такой ключ только на чтение (GET) */}
                                                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                                                        <input type="checkbox"
                                                            checked={scopesDraft.includes('read-only')}
                                                            onChange={() => setScopesDraft(prev =>
                                                                prev.includes('read-only')
                                                                    ? prev.filter(k => k !== 'read-only')
                                                                    : [...prev, 'read-only'])} />
                                                        <b>Read-only</b>
                                                        <span className="admin-panel__muted">the key can only read, any write is rejected</span>
                                                    </label>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <button type="button" className="admin-panel__save-btn" onClick={() => saveScopes(b)}>
                                                            Save
                                                        </button>
                                                        {scopesMsg && (
                                                            <span style={{ color: scopesMsg.startsWith('Error:') ? '#b91c1c' : '#166534', fontSize: 13 }}>
                                                                {scopesMsg}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                    {instructionsFor === b.id && (
                                        <tr className="admin-panel__sub-row">
                                            <td colSpan={7}>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                    <span className="admin-panel__muted">
                                                        Instructions (system prompt) for {b.name} — this exact text is what the bot runs with.
                                                        Clear the field completely to reset to the built-in default. Applied after bot restart.
                                                    </span>
                                                    <textarea
                                                        rows={8}
                                                        value={instructionsDraft}
                                                        placeholder="Leave empty to use the default prompt built into the bot"
                                                        onChange={e => { setInstructionsDraft(e.target.value); setInstructionsMsg(''); }}
                                                        style={{
                                                            width: '100%', boxSizing: 'border-box', padding: '8px 12px',
                                                            border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13,
                                                            fontFamily: 'monospace', resize: 'vertical',
                                                        }}
                                                    />
                                                    <label className="admin-panel__muted" style={{ fontSize: 12 }}>
                                                        Allowed Telegram user IDs (comma-separated) — who can message the bot.
                                                        Also editable from Telegram via /adduser (admins).
                                                    </label>
                                                    <input
                                                        placeholder="e.g. 123456789, 987654321"
                                                        value={allowedDraft}
                                                        onChange={e => { setAllowedDraft(e.target.value); setInstructionsMsg(''); }}
                                                        style={{
                                                            width: '100%', boxSizing: 'border-box', padding: '6px 12px',
                                                            border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13,
                                                        }}
                                                    />
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <button type="button" className="admin-panel__save-btn"
                                                            onClick={() => saveInstructions(b)}>
                                                            Save
                                                        </button>
                                                        {instructionsMsg && (
                                                            <span style={{ color: instructionsMsg.startsWith('Error:') ? '#b91c1c' : '#166534', fontSize: 13 }}>
                                                                {instructionsMsg}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
}

export default AdminUsersPage;
